import {
  AtticSettings,
  GableSettings,
  HeelSettings,
  OverhangSettings,
  PiggybackSettings,
  ProfileDefaults,
} from '@paragon/contract-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { ComponentInsertionShape, ConfigurableShapeDetails } from '../../store/slices/applicationSlice';

export function getDefaultShapeDetails(
  defaults: ProfileDefaults,
  shape: ComponentInsertionShape,
): ConfigurableShapeDetails | null {
  switch (shape) {
    case 'Attic':
      return {
        type: 'Attic',
        span: 24 * 12,
        slopeInDegrees: defaults.topChordSlope,
        heelSettings: getDefaultHeelSettings(defaults),
        overhang: getDefaultOverhangSettings(defaults),
        atticSettings: getDefaultAtticSettings(defaults),
      };

    case 'Common':
      return {
        type: 'Common',
        span: 24 * 12,
        slopeInDegrees: defaults.topChordSlope,
        heelSettings: getDefaultHeelSettings(defaults),
        overhang: getDefaultOverhangSettings(defaults),
      };

    case 'FreeformProfile':
      return {
        type: 'FreeformProfile',
        span: 24 * 12,
      };

    case 'Gable':
      return {
        type: 'Gable',
        span: 24 * 12,
        slopeInDegrees: defaults.topChordSlope,
        heelSettings: getDefaultHeelSettings(defaults),
        overhang: getDefaultOverhangSettings(defaults),
        gableSettings: getDefaultGableSettings(defaults, { isGable: true }),
      };

    case 'Jack':
      return {
        type: 'Jack',
        span: 12 * 12,
        slopeInDegrees: defaults.topChordSlope,
        leftHeelSettings: getDefaultHeelSettings(defaults),
        leftOverhang: getDefaultOverhangSettings(defaults),
      };

    case 'Scissors':
      return {
        type: 'Scissors',
        span: 24 * 12,
        topChordSlopeInDegrees: defaults.topChordSlope,
        bottomChordSlopeInDegrees: defaults.scissorBottomChordSlope,
        heelSettings: getDefaultHeelSettings(defaults),
        overhang: getDefaultOverhangSettings(defaults),
        seatCut: defaults.bearingWidth,
      };

    case 'Valley':
      return {
        type: 'Valley',
        span: 12 * 12,
        slopeInDegrees: defaults.topChordSlope,
      };

    case 'Piggyback':
      return {
        type: 'Piggyback',
        span: 12 * 12,
        slopeInDegrees: defaults.topChordSlope,
        piggybackSettings: getDefaultPiggybackSettings(defaults),
      };

    case 'FeaturePattern':
    case 'G2T-TC-TC':
    case 'G2T-BC-BC':
    case 'G2T-BC-TC':
    case 'G2T-TC-BC':
    case 'PFT':
    case 'PFT-J':
      return null;

    default:
      assertExhaustiveSwitch(shape);
  }
}

export function getDefaultHeelSettings(defaults: ProfileDefaults): HeelSettings {
  switch (defaults.heelMethod) {
    case 'HeelHeight':
      return {
        heelMethod: 'HeelHeight',
        heelHeight: defaults.heelHeight,
        interiorButtCut: defaults.interiorButtCut,
        chordTypeRunThrough: defaults.chordTypeRunThrough,
      };

    case 'ButtCut':
      return {
        heelMethod: 'ButtCut',
        buttCut: defaults.buttCut,
        interiorButtCut: defaults.interiorButtCut,
        chordTypeRunThrough: defaults.chordTypeRunThrough,
      };

    default:
      assertExhaustiveSwitch(defaults.heelMethod);
  }
}

export function getDefaultOverhangSettings(defaults: ProfileDefaults): OverhangSettings {
  return {
    overhangCutType: defaults.overhangCutType,
    distance: defaults.overhangDistance,
  };
}

export function getDefaultGableSettings(defaults: ProfileDefaults, overrides?: Partial<GableSettings>): GableSettings {
  return {
    isGable: false,
    isStructuralGable: false,
    studSpacing: defaults.gableStudSpacing,
    studdingDirection: defaults.gableStuddingDirection,
    studStartLocation: 0,
    minimumStudLength: defaults.gableMinimumStudLength,

    ...overrides,
  };
}
// only for use with trusses that should have attics; leave attic settings `null` for all other trusses
export function getDefaultAtticSettings(defaults: ProfileDefaults): AtticSettings {
  return {
    middle: defaults.atticMiddle,
    width: defaults.atticWidth,
    floorDepth: defaults.atticFloorDepth,
    useSingleFloorChord: defaults.atticUseSingleFloorChord,
    zoneSettings: {
      guid: crypto.randomUUID(),
      horizontalWebLength: defaults.atticHorizontalWebLength,
      maxWebRun: defaults.atticMaxNumberOfWebsInARun,
      webbingType: defaults.atticWebbingType,
      leftWebbingStart: defaults.atticLeftWebbingStart,
      rightWebbingStart: defaults.atticRightWebbingStart,
      chaseSettings: {
        guid: crypto.randomUUID(),
        noChase: true,
        chaseType: defaults.atticChaseType,
        chaseMaxWidth: defaults.atticChaseMaxWidth,
        chaseMinWidth: defaults.atticChaseMinWidth,
        chaseLocationType: defaults.atticChaseLocationType,
        chaseBaseSide: defaults.atticChaseBaseSide,
        chaseLocation: defaults.atticChaseLocation,
      },
    },
    height: defaults.atticHeight,
  };
}

// only for use with trusses that should be piggybacked; leave piggyback settings `null` for all other trusses
export function getDefaultPiggybackSettings(defaults: ProfileDefaults): PiggybackSettings {
  return {
    purlinThickness: defaults.piggybackPurlinThickness,
    runEndVerticalsThrough: defaults.piggybackRunEndVerticalsThrough,
  };
}
